import { RightOutlined } from "@ant-design/icons";
import { Button, ColorPicker, Form, Spin, Switch, Tabs } from "antd";
import { useForm } from "antd/es/form/Form";
import {
	memo,
	MutableRefObject,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import FormButtonCancel from "../components/FormButtonCancel";
import FormButtonSave from "../components/FormButtonSave";
import UploadComponent from "../components/UploadComponent";
import { useThemeContext } from "../contexts/context";
import { updateTenantItem } from "../services/api-server/tenant_management";
import { useTenantStore } from "../stores/tenantStore";
import { alertMessage } from "../utils/alert";
import Emitter from "../utils/emitter";
import { getCrudaClass } from "../utils/lookup_list";
import { getInitials } from "../utils/utils";
import { HomeContext } from "./Home";

// ─── Types ────────────────────────────────────────────────────────────────────

type RightPanelConfig = {
	title: string;
	content: ReactNode;
} | null;

type LogoPropertyName = "logo" | "report_logo";

type LogoDisplayPanelProps = {
	tenant: any;
	propertyName: LogoPropertyName;
	label: string;
	setRightPanel: (config: RightPanelConfig) => void;
	onUpdate: (updatedData: Record<string, any>) => void;
};

type ThemeSettingsPanelProps = {
	tenant: any;
	setRightPanel: (config: RightPanelConfig) => void;
	handleFormUpdating: (bool: boolean, key?: string) => void;
};

type BrandIdentityProps = {
	setRightPanel: (config: RightPanelConfig) => void;
	tenantRef: MutableRefObject<any>;
	onUpdate: (updatedData: Record<string, any>) => void;
	handleFormUpdating: (bool: boolean, key?: string) => void;
};

// ─── Module-level constants ───────────────────────────────────────────────────

// Stable no-op for optional callbacks that must be provided but do nothing here
const noop = () => {};

type BrandItem = {
	key: string;
	title: string;
	description: string;
	propertyName: LogoPropertyName | null;
};

const BRAND_ITEMS: BrandItem[] = [
	{
		key: "tenant_logo",
		title: "Tenant logo",
		description:
			"Upload and manage your tenant's primary logo used across the application.",
		propertyName: "logo",
	},
	{
		key: "report_logo",
		title: "Report logo",
		description:
			"Configure a custom logo specifically for generated reports and documents.",
		propertyName: "report_logo",
	},
	{
		key: "theme_settings",
		title: "Theme settings",
		description:
			"Customize the visual appearance with primary colors, buttons, and branding elements.",
		propertyName: null,
	},
];

// ─── LogoDisplayPanel ─────────────────────────────────────────────────────────

const LogoDisplayPanel = ({
	tenant,
	propertyName,
	label,
	setRightPanel,
	onUpdate,
}: LogoDisplayPanelProps) => {
	const [image, setImage] = useState<any>(tenant?.[propertyName]);
	const initials = getInitials(tenant?.name, 0);

	useEffect(() => {
		setImage(tenant?.[propertyName]);
	}, [tenant, propertyName]);

	const handleImageSave = useCallback(
		async (imageData: any) => {
			try {
				await updateTenantItem(tenant.uuid, {
					...tenant,
					[propertyName]: imageData,
				});
				Emitter.emit("tenantUpdated", null);
				alertMessage("success", `${label} has been successfully updated.`);
				setImage(imageData);
				onUpdate({ [propertyName]: imageData });
			} catch (error) {
				console.error(error);
				alertMessage("error", "Failed to update. Please try again.");
			}
		},
		[tenant, propertyName, label, onUpdate]
	);

	const handleImageRemove = useCallback(async () => {
		try {
			await updateTenantItem(tenant.uuid, { [propertyName]: null });
			Emitter.emit("tenantUpdated", null);
			alertMessage("success", `${label} has been successfully removed.`);
			setImage(null);
			onUpdate({ [propertyName]: null });
		} catch (error) {
			console.error(error);
			alertMessage("error", "Failed to remove. Please try again.");
		}
	}, [tenant, propertyName, label, onUpdate]);

	return (
		<div className="flex flex-col h-full">
			<div style={{ padding: "20px" }}>
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						marginBottom: "20px",
					}}
				>
					<UploadComponent
						label={label.toLowerCase()}
						prefix={""}
						initials={initials}
						image={image}
						setEmptyModalOpen={noop}
						removeMessage={`Remove ${label}? It will be replaced with tenant's initials instead.`}
						setImage={setImage}
						handleImageSave={handleImageSave}
						handleImageRemove={handleImageRemove}
					/>
				</div>
				<div
					style={{
						textAlign: "center",
						color: "grey",
						fontSize: "14px",
					}}
				>
					Click on the {label.toLowerCase()} above to edit
				</div>
			</div>
			<div className="generic-footer gap-2! justify-end mt-auto">
				<Button
					onClick={() => setRightPanel(null)}
					className="ant-btn-secondary"
				>
					Close
				</Button>
			</div>
		</div>
	);
};

// ─── ThemeSettingsPanel ───────────────────────────────────────────────────────

const ThemeSettingsPanel = ({
	tenant,
	setRightPanel,
	handleFormUpdating,
}: ThemeSettingsPanelProps) => {
	const [formRef]: any = useForm();
	const [formReset, setFormReset] = useState<boolean>(true);
	const [showThemeSettings, setShowThemeSettings] = useState<boolean>(false);

	const themeContext: any = useThemeContext();
	const setTenantData = useTenantStore((state) => state.setTenant);
	const tenantList = useTenantStore((state) => state.tenantList);
	const setTenantList = useTenantStore((state) => state.setTenantList);

	// Single stable handler shared across all form fields
	const markDirty = useCallback(() => {
		setFormReset(false);
		// Notify parent that form has unsaved changes
		handleFormUpdating(true, "theme-settings");
	}, [handleFormUpdating]);

	const resetForm = (values: any = null, reset: boolean = true) => {
		if (!formRef) return;
		setFormReset(true);
		if (reset) formRef.resetFields();
		formRef.setFieldsValue(values ? { ...values } : null);
		setShowThemeSettings(values?.default_theme === false);
		// Clear form updating state when form is reset
		handleFormUpdating(false, "theme-settings");
	};

	useEffect(() => {
		if (tenant) resetForm(tenant);
	}, [tenant]);

	const handleFormSave = (values: any) => {
		getCrudaClass("tenant").then((cruda: any) => {
			cruda
				.updateLookupItem("tenant", null, tenant.uuid, null, values)
				.then((data: any) => {
					alertMessage(
						"success",
						"Theme settings have been successfully updated."
					);
					setTenantData({ ...tenant, ...data });
					setTenantList(
						tenantList.map((t) =>
							t.role_key === tenant.role_key ? { ...t, ...data } : t
						)
					);
					resetForm(data);
					themeContext.setDefaultTheme(data.default_theme);
					if (!data.default_theme) {
						themeContext.setThemeVariables(data.theme_settings);
					}
					// Clear form updating state after successful save
					handleFormUpdating(false, "theme-settings");
				})
				.catch((error: any) => {
					console.log(error);
					alertMessage("error", "Failed to update. Please try again.");
				});
		});
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
			<div style={{ flex: "1 1 0", overflowY: "auto", padding: "20px" }}>
				<Form
					className="project-form"
					requiredMark="optional"
					layout="vertical"
					form={formRef}
					onChange={markDirty}
					onSelect={markDirty}
				>
					<Form.Item
						label="Use default theme"
						name="default_theme"
						rules={[{ required: true, message: "" }]}
						valuePropName="checked"
						tooltip="To set custom theme, disable Terra theme."
						layout="horizontal"
						initialValue={true}
					>
						<Switch
							onChange={(checked) => {
								setShowThemeSettings(!checked);
								setFormReset(false);
							}}
						/>
					</Form.Item>

					<div
						className="theme-pallete-container"
						style={{ display: showThemeSettings ? "block" : "none" }}
					>
						<div>Set custom theme</div>
						<Form.Item
							className="horizontal-label color-picker"
							label="Primary colour"
							name={["theme_settings", "primary_color"]}
							rules={[{ required: true, message: "" }]}
							tooltip="Sets the main theme of the app, representing primary branding and style."
							getValueFromEvent={(color) => color.toHexString()}
							initialValue="#00003C"
						>
							<ColorPicker showText onChange={markDirty} />
						</Form.Item>
						<Form.Item
							className="horizontal-label color-picker"
							label="Secondary colour"
							name={["theme_settings", "secondary_color"]}
							rules={[{ required: true, message: "" }]}
							tooltip="Serves as the complementary accent to the primary colour, used to highlight additional elements."
							getValueFromEvent={(color) => color.toHexString()}
							initialValue="#00003C"
						>
							<ColorPicker showText onChange={markDirty} />
						</Form.Item>
						<Form.Item
							className="horizontal-label color-picker"
							label="Default button"
							name={["theme_settings", "default_btn_color"]}
							rules={[{ required: true, message: "" }]}
							tooltip="Sets the base colour for standard buttons."
							getValueFromEvent={(color) => color.toHexString()}
							initialValue="#00BEBE"
						>
							<ColorPicker showText onChange={markDirty} />
						</Form.Item>
						<Form.Item
							className="horizontal-label color-picker"
							label="Danger button"
							name={["theme_settings", "danger_btn_color"]}
							rules={[{ required: true, message: "" }]}
							tooltip="Sets the colour for actions that have significant consequences, such as deletions or irreversible changes."
							getValueFromEvent={(color) => color.toHexString()}
							initialValue="#FF4D4F"
						>
							<ColorPicker showText onChange={markDirty} />
						</Form.Item>
					</div>
				</Form>
			</div>
			<div className="generic-footer gap-2! justify-end">
				{!formReset ? (
					<FormButtonCancel
						form={formRef}
						handleFormCancel={() => resetForm(tenant)}
						formReset={formReset}
					/>
				) : (
					<Button
						className="ant-btn-secondary"
						onClick={() => setRightPanel(null)}
					>
						Close
					</Button>
				)}
				<FormButtonSave
					form={formRef}
					handleFormSave={handleFormSave}
					formReset={formReset}
				/>
			</div>
		</div>
	);
};

// ─── BrandIdentity ────────────────────────────────────────────────────────────

const BrandIdentity = memo(
	({
		setRightPanel,
		tenantRef,
		onUpdate,
		handleFormUpdating,
	}: BrandIdentityProps) => {
		return (
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "flex-start",
					gap: "10px",
					alignSelf: "stretch",
					width: "100%",
					maxWidth: "800px",
				}}
			>
				{BRAND_ITEMS.map((item) => (
					<div
						key={item.key}
						className="hover:bg-white/5"
						onClick={() => {
							// Read ref at click-time so the panel always opens with the latest data
							const tenant = tenantRef.current;
							if (item.propertyName) {
								setRightPanel({
									title: item.title,
									content: (
										<LogoDisplayPanel
											tenant={tenant}
											propertyName={item.propertyName}
											label={item.title}
											setRightPanel={setRightPanel}
											onUpdate={onUpdate}
										/>
									),
								});
							} else {
								setRightPanel({
									title: item.title,
									content: (
										<ThemeSettingsPanel
											tenant={tenant}
											setRightPanel={setRightPanel}
											handleFormUpdating={handleFormUpdating}
										/>
									),
								});
							}
						}}
						style={{
							display: "flex",
							padding: "16px 24px",
							alignItems: "center",
							gap: "20px",
							alignSelf: "stretch",
							borderRadius: "3px",
							border: "1px solid grey",
							cursor: "pointer",
							transition: "background-color 0.2s ease",
						}}
					>
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "flex-start",
								gap: "4px",
								flex: "1 1 0",
							}}
						>
							<div style={{ fontWeight: 500 }}>{item.title}</div>
							<div style={{ color: "grey", fontSize: "14px" }}>
								{item.description}
							</div>
						</div>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								color: "grey",
							}}
						>
							<RightOutlined style={{ fontSize: "16px" }} />
						</div>
					</div>
				))}
			</div>
		);
	}
);

// ─── TenantManagement ─────────────────────────────────────────────────────────

const TenantManagement = () => {
	const [rightPanel, setRightPanel] = useState<RightPanelConfig>(null);
	const [activeTab, setActiveTab] = useState<string>("brand_identity");
	const [currentTenant, setCurrentTenant] = useState<any>(null);
	// Ref holds the latest tenant data for BrandIdentity to read at click-time,
	// avoiding re-renders when logo/theme updates happen
	const currentTenantRef = useRef<any>(null);

	const context: any = useContext(HomeContext);

	useEffect(() => {
		getCrudaClass("tenant").then((cruda: any) => {
			cruda
				.getLookupItem("tenant", context?.currentTenantKey)
				.then((_data: any) => {
					const tenantData = _data.tableData;
					if (tenantData) {
						const found = tenantData.find(
							(t: any) => t.role_key === context.currentTenantKey
						);
						setCurrentTenant(found);
						currentTenantRef.current = found;
					}
				})
				.catch((error: any) => {
					console.log(error);
				});
		});
	}, [context.currentTenantKey]);

	// Write to ref only — no state update, so no cascading re-renders in other tabs
	const handleTenantUpdate = useCallback((updatedData: Record<string, any>) => {
		if (currentTenantRef.current) {
			currentTenantRef.current = {
				...currentTenantRef.current,
				...updatedData,
			};
		}
	}, []);

	const handleTabChange = useCallback((key: string) => {
		setActiveTab(key);
		setRightPanel(null);
	}, []);

	// Only recreates when the active tab's data actually changes, not on every render
	const items = useMemo(
		() => [
			{
				key: "brand_identity",
				label: "Brand & Identity",
				children: currentTenant ? (
					<BrandIdentity
						setRightPanel={setRightPanel}
						tenantRef={currentTenantRef}
						onUpdate={handleTenantUpdate}
						handleFormUpdating={context?.handleFormUpdating}
					/>
				) : (
					<div className="flex justify-center items-center h-full w-full">
						<Spin />
					</div>
				),
			},
		],
		[
			currentTenant,
			setRightPanel,
			handleTenantUpdate,
			context?.handleFormUpdating,
		]
	);

	return (
		<div className="crud-tab-table-modal-container">
			<div className="main-container">
				<div className="generic-header">Tenant Management</div>
				<div
					className="generic-content"
					style={{
						display: "flex",
						padding: "0",
						flexDirection: "row",
						flex: "1 1 0",
						alignSelf: "stretch",
						overflowY: "auto",
					}}
				>
					<Tabs
						tabPosition="left"
						items={items}
						className="tenant-management-tabs"
						style={{ width: "100%", height: "100%" }}
						activeKey={activeTab}
						onChange={handleTabChange}
					/>
				</div>
			</div>

			{/* Right Panel */}
			<div
				className={`side-container transition-all duration-300 ease-in-out min-w-1/3 max-w-1/3 h-full ${
					rightPanel
						? "translate-x-0 opacity-100"
						: "translate-x-full opacity-0"
				}`}
			>
				<div
					className={`generic-header flex justify-between items-center h-[47px] ${
						!rightPanel ? "shadow-none!" : ""
					}`}
				>
					<span>{rightPanel?.title || "Details"}</span>
				</div>
				<div className="w-full overflow-hidden flex-1">
					{rightPanel?.content}
				</div>
			</div>
		</div>
	);
};

export default TenantManagement;
